import React, { createContext, useState, useEffect, useContext } from 'react';
import { authService, getErrorMessage } from '../services/authService';
import { userService } from '../services/userService';
import { loggerStatic as logger, useLogger } from '../utils/logger';
import cognitoConfig from '../utils/cognitoConfig';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const log = useLogger('AuthContext');
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadUser = async () => {
      try {
        log.debug('Loading current user');
        
        // First check if we have authentication data in sessionStorage
        const isAuthenticated = sessionStorage.getItem('isAuthenticated') === 'true';
        log.debug('Authentication status from sessionStorage', { isAuthenticated });
        
        if (isAuthenticated) {
          // Try to get the current user from Cognito
          const userData = await authService.getCurrentUser();
          
          if (userData) {
            log.debug('User loaded successfully from Cognito');
            setCurrentUser(userData);
          } else {
            log.debug('No valid Cognito session, but found sessionStorage data');
            
            // If we have a username in sessionStorage, try to recreate a minimal user object
            const username = sessionStorage.getItem('username');
            if (username) {
              const minimalUser = {
                userData: {
                  username,
                  email: sessionStorage.getItem('user_email'),
                  name: sessionStorage.getItem('user_name'),
                  sub: sessionStorage.getItem('user_sub')
                },
                accessToken: sessionStorage.getItem('accessToken'),
                idToken: sessionStorage.getItem('idToken'),
                refreshToken: sessionStorage.getItem('refreshToken')
              };
              
              log.debug('Created minimal user from sessionStorage');
              setCurrentUser(minimalUser);
            } else {
              log.debug('No username in sessionStorage, clearing auth state');
              sessionStorage.removeItem('isAuthenticated');
              setCurrentUser(null);
            }
          }
        } else {
          log.debug('No authentication data in sessionStorage');
          setCurrentUser(null);
        }
      } catch (err) {
        log.error('Error loading user', err);
        setError(err);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
    
    // Add event listener for storage changes (for multi-tab support)
    const handleStorageChange = (e) => {
      if (e.key === 'isAuthenticated') {
        log.debug('isAuthenticated changed in another tab');
        loadUser();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const signUp = async (email, password, attributes) => {
    try {
      log.info('Sign up initiated');
      setError(null);
      
      // Ensure password meets Cognito requirements
      if (!/(?=.*[a-z])/.test(password)) {
        const err = new Error('Password must contain at least one lowercase letter');
        err.code = 'InvalidPasswordException';
        throw err;
      }
      if (!/(?=.*[A-Z])/.test(password)) {
        const err = new Error('Password must contain at least one uppercase letter');
        err.code = 'InvalidPasswordException';
        throw err;
      }
      if (!/(?=.*[0-9])/.test(password)) {
        const err = new Error('Password must contain at least one number');
        err.code = 'InvalidPasswordException';
        throw err;
      }
      if (!/(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/.test(password)) {
        const err = new Error('Password must contain at least one special character');
        err.code = 'InvalidPasswordException';
        throw err;
      }
      if (password.length < 8) {
        const err = new Error('Password must be at least 8 characters long');
        err.code = 'InvalidPasswordException';
        throw err;
      }
      
      // 1. Sign up with Cognito
      const user = await authService.signUp(email, password, attributes);
      
      // 2. Prepare user data for DynamoDB
      const userData = {
        email: email.toLowerCase(),
        firstName: attributes.given_name,
        lastName: attributes.family_name,
        plan: attributes['custom:plan'] || 'basic',
        createdAt: new Date().toISOString()
      };
      
      log.info('Saving user data to DynamoDB');
      
      // 3. Save user data to DynamoDB
      try {
        // If we have a token from sign-up, use it
        if (user && user.idToken) {
          await userService.saveUserSignup(userData, user.idToken);
        } else {
          // Otherwise use the token-less method
          await userService.saveUserSignupWithoutToken(userData);
        }
        log.info('User data successfully saved to DynamoDB');
        
        // Store a flag indicating this is a new user
        sessionStorage.setItem('isNewUser', 'true');
        sessionStorage.setItem('signupTimestamp', Date.now().toString());
        
        // Add a small delay to ensure DynamoDB has time to propagate the data
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (saveError) {
        log.error('Error saving user data to DynamoDB during sign-up', saveError);
        // Continue with the flow, but log the error
      }
      
      return user;
    } catch (err) {
      logger.error('AuthContext signUp error:', err);
      setError(err);
      throw err;
    }
  };

  const confirmRegistration = async (email, code) => {
    try {
      setError(null);
      return await authService.confirmRegistration(email, code);
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const signIn = async (email, password) => {
    try {
      setError(null);
      log.info('Attempting to sign in user');
      
      // Make sure email is trimmed to remove any whitespace
      const trimmedEmail = email.trim();
      
      const result = await authService.signIn(trimmedEmail, password);
      log.info('Sign in successful');
      
      // Store the authentication state in sessionStorage
      sessionStorage.setItem('isAuthenticated', 'true');
      sessionStorage.setItem('loginTimestamp', Date.now().toString());
      
      setCurrentUser(result);
      
      // Check if this is a new user from sign-up
      const isNewUser = sessionStorage.getItem('isNewUser') === 'true';
      const signupTimestamp = sessionStorage.getItem('signupTimestamp');
      const isRecentSignup = signupTimestamp && (Date.now() - parseInt(signupTimestamp)) < 60000; // Within 1 minute
      
      if (isNewUser && isRecentSignup) {
        log.debug('New user from recent sign-up, adding delay before proceeding');
        // Add a delay to ensure DynamoDB has time to propagate the data
        await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
        
        // Clear the new user flag
        sessionStorage.removeItem('isNewUser');
        sessionStorage.removeItem('signupTimestamp');
      } else {
        // Check if user data exists in DynamoDB, create it if it doesn't
        try {
          // First try to get the user data
          await userService.getUserData(result.idToken);
          log.debug('User data found in DynamoDB');
        } catch (getUserError) {
          // If user not found in DynamoDB, create the record
          if (getUserError.message && getUserError.message.includes('404')) {
            log.info('User authenticated but not found in DynamoDB, creating record');
            
            try {
              // Extract user attributes from the token
              const decodedToken = JSON.parse(atob(result.idToken.split('.')[1]));
              
              const userData = {
                email: decodedToken.email.toLowerCase(),
                firstName: decodedToken.given_name || '',
                lastName: decodedToken.family_name || '',
                plan: decodedToken['custom:plan'] || 'basic',
                createdAt: new Date().toISOString()
              };
              
              // Save to DynamoDB
              await userService.saveUserSignup(userData, result.idToken);
              log.info('Created missing user record in DynamoDB');
            } catch (saveError) {
              log.error('Error creating user record in DynamoDB', saveError);
            }
          } else {
            log.error('Error checking user data', getUserError);
          }
        }
      }
      
      // Email verification removed
      
      return result;
    } catch (err) {
      logger.error('AuthContext: Sign in failed', err);
      
      // If the user is not confirmed but credentials are valid, try to auto-confirm
      if (err.code === 'UserNotConfirmedException') {
        logger.info('AuthContext: User not confirmed, attempting auto-confirmation');
        
        try {
          // Try to confirm the user automatically
          await authService.autoConfirmUser(email.trim());
          
          // After confirmation, try signing in again
          const secondAttemptResult = await authService.signIn(email.trim(), password);
          logger.info('AuthContext: Sign in successful after auto-confirmation');
          setCurrentUser(secondAttemptResult);
          
          // Email verification removed
          
          return secondAttemptResult;
        } catch (confirmError) {
          logger.error('AuthContext: Auto-confirmation failed:', confirmError);
          // If auto-confirmation fails, we'll still throw the original error
        }
      }
      
      setError(err);
      throw err;
    }
  };

  const signOut = () => {
    logger.debug('AuthContext: Signing out user');
    authService.signOut();
    
    // Clear the authentication flag from sessionStorage
    sessionStorage.removeItem('isAuthenticated');
    
    setCurrentUser(null);
  };

  const forgotPassword = async (email) => {
    try {
      logger.info('AuthContext: Initiating forgot password flow for:', email);
      setError(null);
      return await authService.forgotPassword(email);
    } catch (err) {
      logger.error('AuthContext: Error in forgot password flow:', err);
      setError(err);
      throw err;
    }
  };

  const confirmPassword = async (email, verificationCode, newPassword) => {
    try {
      logger.info('AuthContext: Confirming new password for:', email);
      setError(null);
      return await authService.confirmPassword(email, verificationCode, newPassword);
    } catch (err) {
      logger.error('AuthContext: Error confirming new password:', err);
      setError(err);
      throw err;
    }
  };

  const getCurrentUser = async () => {
    try {
      logger.info('AuthContext: Getting current user');
      return await authService.getCurrentUser();
    } catch (err) {
      logger.error('AuthContext: Error getting current user:', err);
      throw err;
    }
  };

  const value = {
    currentUser,
    setCurrentUser,
    loading,
    error,
    signUp,
    confirmRegistration,
    signIn,
    signOut,
    forgotPassword,
    confirmPassword,
    getCurrentUser,
    isAuthenticated: !!currentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
